import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { CircularProgress, Box } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { setUser, initializeStoredToken, logoutUser, setToken, setError, fetchMongoUser } from '../store/authSlice';
import { auth } from '../config/firebase';
import { validateAndRefreshToken } from '../utils/tokenUtils';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, tokenInfo, user, isMongoVerified } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [tokenValidated, setTokenValidated] = useState(false);
  const [authListenerSetup, setAuthListenerSetup] = useState(false);

  // Setup auth state listener - only once on mount
  useEffect(() => {
    // Initialize stored token first (only once)
    dispatch(initializeStoredToken());

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {

      if (firebaseUser) {
        try {
          // Validate and refresh token
          const { isValid, token } = await validateAndRefreshToken();

          if (!isValid) {
            console.warn('[ProtectedRoute] ❌ JWT token invalid, logging out user');
            await dispatch(logoutUser());
            setTokenValidated(false);
            setLoading(false);
            return;
          }

          // Token is valid, update state
          dispatch(setUser(firebaseUser));

          // Get fresh token info
          const tokenResult = await firebaseUser.getIdTokenResult();
          dispatch(setToken({
            token: token!,
            expiresAt: new Date(tokenResult.expirationTime).getTime(),
            issuedAt: new Date(tokenResult.issuedAtTime).getTime()
          }));

          // Fetch and verify MongoDB user (allows all active users)
          const mongoResult = await dispatch(fetchMongoUser());

          if (fetchMongoUser.fulfilled.match(mongoResult)) {
            console.log('[ProtectedRoute] ✅ User access verified:', mongoResult.payload.user.email);
            console.log('[ProtectedRoute] ✅ User role:', mongoResult.payload.user.role);
            setTokenValidated(true);
          } else if (fetchMongoUser.rejected.match(mongoResult)) {
            console.warn('[ProtectedRoute] ❌ User access verification failed');
            await dispatch(logoutUser());
            window.alert(mongoResult.payload as string || 'Access denied. Please contact your administrator.');
            dispatch(setError(mongoResult.payload as string || 'Access verification failed'));
            setTokenValidated(false);
          }
        } catch (error) {
          console.error('[ProtectedRoute] Error during authentication:', error);
          setTokenValidated(false);
        }
      } else {
        // No user, clear state
        console.log('[ProtectedRoute] No Firebase user, clearing state');
        dispatch(setUser(null));
        setTokenValidated(false);
      }

      setLoading(false);
      setAuthListenerSetup(true);
    });

    return () => {
      console.log('[ProtectedRoute] Component unmounting, cleaning up auth listener');
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]); // Only depend on dispatch to avoid re-registering listener

  // Periodic token refresh - check every minute
  useEffect(() => {
    if (!isAuthenticated || !tokenValidated) {
      return;
    }
    
  
    const intervalId = setInterval(async () => {
    
      const { isValid } = await validateAndRefreshToken();
      
      if (!isValid) {
        console.warn('[ProtectedRoute] ❌ Token validation failed during periodic check, logging out');
        dispatch(logoutUser());
      } else {
        console.log('[ProtectedRoute] ✅ Periodic token validation successful');
      }
    }, 60000); // Check every minute
    
    return () => {
      console.log('[ProtectedRoute] Clearing periodic token validation');
      clearInterval(intervalId);
    };
  }, [isAuthenticated, tokenValidated, dispatch]);

  // Debug logging for render states
  const shouldAllow = tokenValidated && auth.currentUser;
  if (loading || !authListenerSetup) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        className="bg-gray-50"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Check tokenValidated AND Firebase currentUser (more reliable than Redux state timing)
  if (shouldAllow) {
    return <>{children}</>;
  } else {
    console.log('[ProtectedRoute] ❌ Redirecting to login');
    return <Navigate to="/login" replace />;
  }
};

export default ProtectedRoute;